import React, { useState, useEffect, useMemo, useId } from "react";
import styles from "./Calculator.module.css";

export type DonutSegment = {
  label: string;
  value: string;
  color: string;
  rawValue: number;
};

export type CalculatorDonutChartProps = {
  segments: DonutSegment[];
  centerLabel?: string;
  centerValue: string;
  benchmarkText?: React.ReactNode;
};

const GAP = 2;
const CX = 100;
const CY = 100;
const R_OUTER = 90;
const R_INNER = 58;

const polarXY = (cx: number, cy: number, r: number, deg: number): [number, number] => {
  const rad = (deg - 90) * (Math.PI / 180);
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

const makePath = (cx: number, cy: number, outerRadius: number, innerRadius: number, startAngle: number, endAngle: number): string => {
  const [o1x, o1y] = polarXY(cx, cy, outerRadius, startAngle);
  const [o2x, o2y] = polarXY(cx, cy, outerRadius, endAngle);
  const [i1x, i1y] = polarXY(cx, cy, innerRadius, endAngle);
  const [i2x, i2y] = polarXY(cx, cy, innerRadius, startAngle);
  const isLargeArc = endAngle - startAngle > 180 ? 1 : 0;
  return [
    `M ${o1x.toFixed(2)} ${o1y.toFixed(2)}`,
    `A ${outerRadius} ${outerRadius} 0 ${isLargeArc} 1 ${o2x.toFixed(2)} ${o2y.toFixed(2)}`,
    `L ${i1x.toFixed(2)} ${i1y.toFixed(2)}`,
    `A ${innerRadius} ${innerRadius} 0 ${isLargeArc} 0 ${i2x.toFixed(2)} ${i2y.toFixed(2)}`,
    "Z",
  ].join(" ");
};

export const CalculatorDonutChart: React.FC<CalculatorDonutChartProps> = ({
  segments,
  centerLabel = "Annual Cost",
  centerValue,
  benchmarkText,
}) => {
  const uniqueId = useId();
  const shadowFilterId = `pie-shadow${uniqueId}`;

  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(null);
  const [isPulsing, setIsPulsing] = useState(false);

  useEffect(() => {
    setIsPulsing(false);
    const frameId = requestAnimationFrame(() => setIsPulsing(true));
    return () => cancelAnimationFrame(frameId);
  }, [centerValue]);

  const paths = useMemo(() => {
    const total = segments.reduce((accumulated, seg) => accumulated + seg.rawValue, 0);
    const shares =
      total > 0
        ? segments.map((seg) => seg.rawValue / total)
        : segments.map(() => 1 / segments.length);

    return shares.map((share, i) => {
      const startDeg = shares.slice(0, i).reduce((accumulated, s) => accumulated + s * 360, 0);
      const sweepDeg = share * 360;
      if (sweepDeg < 0.5) return null;
      return makePath(CX, CY, R_OUTER, R_INNER, startDeg + GAP / 2, startDeg + sweepDeg - GAP / 2);
    });
  }, [segments]);

  return (
    <>
      <div className={styles.donutWrapper}>
        <div
          className={`${styles.donutGlow} ${isPulsing ? styles.donutGlowPulse : ""}`}
          onAnimationEnd={() => setIsPulsing(false)}
        />
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" style={{ width: 200, height: 200, display: "block" }}>
          <defs>
            <filter id={shadowFilterId}>
              <feDropShadow dx="0" dy="2" stdDeviation="5" floodColor="rgba(0,0,0,0.3)" />
            </filter>
          </defs>
          <circle
            cx={CX}
            cy={CY}
            r={(R_OUTER + R_INNER) / 2}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={R_OUTER - R_INNER}
          />
          {paths.map((d, i) =>
            d ? (
              <path
                key={i}
                d={d}
                fill={segments[i].color}
                filter={`url(#${shadowFilterId})`}
                style={{
                  cursor: "pointer",
                  transformOrigin: `${CX}px ${CY}px`,
                  transform: hoveredSegmentIndex === i ? "scale(1.06)" : "scale(1)",
                  opacity: hoveredSegmentIndex !== null && hoveredSegmentIndex !== i ? 0.5 : 1,
                  transition: "transform 0.2s, opacity 0.2s",
                }}
                onMouseEnter={() => setHoveredSegmentIndex(i)}
                onMouseLeave={() => setHoveredSegmentIndex(null)}
              />
            ) : null
          )}
        </svg>
        <div className={styles.donutCenter}>
          <div className={styles.donutCenterLabel}>{centerLabel}</div>
          <div className={styles.donutCenterValue}>{centerValue}</div>
          {benchmarkText && (
            <div className={styles.donutCenterBenchmark}>{benchmarkText}</div>
          )}
        </div>
      </div>

      <div className={styles.donutLegend}>
        {segments.map((seg, i) => (
          <div
            key={seg.label}
            className={`${styles.legendItem} ${hoveredSegmentIndex === i ? styles.legendItemHighlight : ""}`}
            onMouseEnter={() => setHoveredSegmentIndex(i)}
            onMouseLeave={() => setHoveredSegmentIndex(null)}
          >
            <span className={styles.legendColorSwatch} style={{ background: seg.color }} />
            <div>
              <div className={styles.legendLabel}>{seg.label}</div>
              <div className={styles.legendValue}>{seg.value}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
